import axios from 'axios';
import React from 'react';
import Draggable from 'react-draggable';
import { toast } from 'react-toastify';
import { Card, CardBody, CardHeader, Modal } from 'reactstrap';
import { REQUEST_URL } from '../../config/urls';
import ModalAdicionarItem from '../ModalAdicionarItem';

function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

// Extrai YYYY-MM-DD de uma data vinda do backend, sem passar por new Date().
//
// O driver MySQL entrega colunas DATE já como objeto Date em UTC 00:00. No
// fuso do Brasil (UTC-3) isso vira 21h do dia anterior, e toLocaleDateString
// mostrava um dia a menos. Como dataAdendo é uma data pura (sem hora), o certo
// é ler os componentes da string e nunca converter fuso.
function partesData(valor) {
  if (!valor) return null;
  const texto =
    typeof valor === 'string' ? valor : new Date(valor).toISOString();
  const m = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { ano: m[1], mes: m[2], dia: m[3] } : null;
}

function formatarData(valor) {
  const p = partesData(valor);
  return p ? `${p.dia}/${p.mes}/${p.ano}` : '-';
}

// YYYY-MM-DD para popular <input type="date">
function dataParaInput(valor) {
  const p = partesData(valor);
  return p ? `${p.ano}-${p.mes}-${p.dia}` : '';
}

// Espelha OFFSET_ADENDO do backend (AdendoRoute.js): itens de adendo ocupam
// essa faixa de refIndexPlano na tabela Vendas_Webloja_Itens.
const OFFSET_ADENDO = 2000000;

// Cores por natureza do item, para distinguir na tabela.
const CORES_TIPO = {
  PRODUTO: '#27ae60',
  ACABAMENTO: '#17a2b8',
  QTD: '#e67e22',
};

const CORES_STATUS = {
  rascunho: '#e67e22',
  aprovado: '#27ae60',
  cancelado: '#95a5a6',
};

class TabelaAdendos extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      adendos: [],
      carregando: false,
      expandidos: {},
      salvando: false,
      // adendo que está recebendo item pelo modal de produtos
      adendoRecebendoItem: null,
      showModalAddItem: false,
      // Definido pelo servidor: só ORCAMENTISTA pode criar, editar, aprovar,
      // cancelar e excluir. Vendedor apenas visualiza.
      podeEditar: false,
      podeExcluir: false,
      // edição inline de descrição/data
      editandoId: null,
      formEdicao: { descricao: '', dataAdendo: '', motivo: '' },
      // edição inline de qtd/preço do item
      editandoItemId: null,
      formItemEdicao: {
        qtd: '',
        precoUnit: '',
        qtdBrinde: '',
        precoTotal: '',
        // Qual campo o usuário digitou por último: 'unit' calcula o subtotal,
        // 'total' calcula o unitário. Evita que um sobrescreva o outro.
        origem: 'unit',
      },
      // adendo de ACABAMENTO / QTD: escolha do item base do orçamento
      tipoEscolhaDe: null, // idAdendo aguardando escolha do tipo
      formRef: {
        tipo: null, // 'ACABAMENTO' | 'QTD'
        idAdendo: null,
        refIndexPlano: '',
        descricao: '',
        qtd: '',
        precoUnit: '',
      },
      itensBase: [],
      acabamentosDisponiveis: [],
      // Web loja: itens do contrato liberados para a turma comprar online.
      // A compra que chegar vira item de um adendo automatico.
      mostrarWebloja: false,
      weblojaItens: [],
      weblojaCarregando: false,
      weblojaSalvandoRef: null,
    };
  }

  componentDidMount() {
    this.carregar();
    this.carregarPermissoes();
  }

  carregarPermissoes() {
    axios
      .get(`${REQUEST_URL}/adendos/permissoes`, { headers: this.headers() })
      .then((resp) =>
        this.setState({
          podeEditar: !!(resp.data && resp.data.podeEditar),
          podeExcluir: !!(resp.data && resp.data.podeExcluir),
        })
      )
      .catch(() => this.setState({ podeEditar: false, podeExcluir: false }));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.idOrc !== this.props.idOrc) {
      this.carregar();
      // Revalida a permissão junto: sem isso, trocar de orçamento sem recarregar
      // a página manteria o podeExcluir da sessão anterior.
      this.carregarPermissoes();
    }
  }

  headers() {
    return { Authorization: sessionStorage.getItem('token') };
  }

  carregar(idExpandir) {
    const { idOrc } = this.props;
    if (!idOrc) return;

    this.setState({ carregando: true });
    axios
      .get(`${REQUEST_URL}/orcamento/${idOrc}/adendos`, {
        headers: this.headers(),
      })
      .then((resp) => {
        const adendos = Array.isArray(resp.data) ? resp.data : [];
        this.setState((estado) => ({
          adendos,
          carregando: false,
          // mantém aberto o adendo recém-criado/alterado
          expandidos: idExpandir
            ? { ...estado.expandidos, [idExpandir]: true }
            : estado.expandidos,
        }));
        this.notificarMudanca();
      })
      .catch((err) => {
        console.error('[TabelaAdendos] carregar', err);
        this.setState({ carregando: false });
        toast.error('Não foi possível carregar os adendos.');
      });
  }

  notificarMudanca() {
    if (this.props.onChange) this.props.onChange();
  }

  // -------------------------------------------------------------------------
  // Web loja
  // -------------------------------------------------------------------------

  carregarWebloja() {
    const { idOrc } = this.props;
    if (!idOrc) return;

    this.setState({ weblojaCarregando: true });
    axios
      .get(`${REQUEST_URL}/orcamento/${idOrc}/webloja/itens`, {
        headers: this.headers(),
      })
      .then((resp) =>
        this.setState({
          weblojaItens: Array.isArray(resp.data) ? resp.data : [],
          weblojaCarregando: false,
        })
      )
      .catch((err) => {
        console.error('[TabelaAdendos] carregarWebloja', err);
        this.setState({ weblojaCarregando: false });
        toast.error('Não foi possível carregar os itens da web loja.');
      });
  }

  alternarWebloja() {
    const abrindo = !this.state.mostrarWebloja;
    this.setState({ mostrarWebloja: abrindo }, () => {
      // Só busca ao abrir: evita uma chamada a mais em todo orçamento aberto.
      if (abrindo && this.state.weblojaItens.length === 0) {
        this.carregarWebloja();
      }
    });
  }

  // Liga/desliga a venda do item na loja. O preço da loja é opcional: quando
  // vazio, a loja usa o preço do contrato.
  alternarItemWebloja(item, ativo, precoLoja) {
    const { idOrc } = this.props;
    const ref = item.refIndexPlano;

    this.setState({ weblojaSalvandoRef: ref });
    axios
      .put(
        `${REQUEST_URL}/orcamento/${idOrc}/webloja/itens/${ref}`,
        {
          ativo,
          precoLoja: precoLoja === undefined ? item.precoLoja : precoLoja,
          limiteQtd: item.limiteQtd,
        },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState((estado) => ({
          weblojaSalvandoRef: null,
          weblojaItens: estado.weblojaItens.map((i) =>
            i.refIndexPlano === ref ? { ...i, ativo: ativo ? 1 : 0 } : i
          ),
        }));
        toast.success(
          ativo
            ? 'Item liberado para venda na loja.'
            : 'Item removido da loja.'
        );
      })
      .catch((err) => {
        console.error('[TabelaAdendos] alternarItemWebloja', err);
        this.setState({ weblojaSalvandoRef: null });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Não foi possível atualizar o item.';
        toast.error(msg);
      });
  }

  // Libera/retira da loja um item que esta dentro de um adendo. Usa a mesma
  // rota e a mesma tabela dos itens do contrato: o que muda e a faixa do
  // refIndexPlano, que identifica a origem (ver OFFSET_ADENDO no backend).
  alternarItemAdendoNaLoja(idAdendo, item, ativo) {
    const { idOrc } = this.props;
    const ref = OFFSET_ADENDO + item.idItem;

    this.setState({ weblojaSalvandoRef: ref });
    axios
      .put(
        `${REQUEST_URL}/orcamento/${idOrc}/webloja/itens/${ref}`,
        { ativo, precoLoja: item.precoLoja, limiteQtd: null },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({ weblojaSalvandoRef: null });
        toast.success(
          ativo ? 'Item liberado para venda na loja.' : 'Item removido da loja.'
        );
        // Recarrega os adendos (traz o naLoja atualizado) e, se o painel da
        // loja estiver aberto, tambem a lista de la.
        this.carregar(idAdendo);
        if (this.state.mostrarWebloja) this.carregarWebloja();
      })
      .catch((err) => {
        console.error('[TabelaAdendos] alternarItemAdendoNaLoja', err);
        this.setState({ weblojaSalvandoRef: null });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Nao foi possivel atualizar o item.';
        toast.error(msg);
      });
  }

  toggleExpandir(id) {
    this.setState((estado) => ({
      expandidos: { ...estado.expandidos, [id]: !estado.expandidos[id] },
    }));
  }

  // Cria o adendo já com data de hoje e descrição sequencial. Sem formulário:
  // o que caracteriza o adendo são os itens, que entram logo em seguida.
  criarAdendo() {
    if (this.state.salvando) return;

    this.setState({ salvando: true });
    axios
      .post(
        `${REQUEST_URL}/orcamento/${this.props.idOrc}/adendos`,
        {
          criadoPor: this.props.usuarioLogado || null,
          codVendedor: this.props.codVendedor || null,
        },
        { headers: this.headers() }
      )
      .then((resp) => {
        this.setState({ salvando: false });
        const idAdendo = resp.data && resp.data.idAdendo;
        // Abre a escolha de tipo (Produto / Acabamento / Quantidade) do adendo
        // recém-criado. Antes isso abria o modal de produtos direto, o que
        // pulava a escolha e fazia parecer que só existia o tipo PRODUTO.
        // carregar(idAdendo) já deixa este adendo expandido, para a faixa de
        // escolha ficar visível.
        this.setState({ tipoEscolhaDe: idAdendo });
        this.carregar(idAdendo);
        toast.info('Escolha o que este adendo inclui.');
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao criar adendo.';
        toast.error(msg);
      });
  }

  abrirModalItem(idAdendo) {
    this.setState({ adendoRecebendoItem: idAdendo, showModalAddItem: true });
  }

  // Abre o seletor de tipo (Produto / Acabamento / Qtd) para um adendo.
  abrirEscolhaTipo(idAdendo) {
    this.setState({ tipoEscolhaDe: idAdendo });
  }

  // ACABAMENTO e QTD incidem sobre um item do orçamento original, então
  // carregamos a lista de itens base para o usuário escolher.
  escolherTipo(idAdendo, tipo) {
    if (tipo === 'PRODUTO') {
      this.setState({ tipoEscolhaDe: null });
      this.abrirModalItem(idAdendo);
      return;
    }

    this.setState({
      tipoEscolhaDe: null,
      formRef: {
        tipo,
        idAdendo,
        refIndexPlano: '',
        descricao: '',
        qtd: '',
        precoUnit: '',
      },
    });

    axios
      .get(`${REQUEST_URL}/orcamento/${this.props.idOrc}/itens-base`, {
        headers: this.headers(),
      })
      .then((resp) =>
        this.setState({ itensBase: Array.isArray(resp.data) ? resp.data : [] })
      )
      .catch(() => toast.error('Não foi possível carregar os itens do orçamento.'));

    // Para ACABAMENTO, busca a lista de acabamentos disponíveis do serviço.
    if (tipo === 'ACABAMENTO') {
      axios
        .post(
          `${REQUEST_URL}/Produto/lista_acabamentos`,
          { servico: 'CAPA DURA' },
          { headers: this.headers() }
        )
        .then((resp) =>
          this.setState({
            acabamentosDisponiveis: Array.isArray(resp.data) ? resp.data : [],
          })
        )
        .catch(() => this.setState({ acabamentosDisponiveis: [] }));
    }
  }

  salvarItemReferenciado() {
    const f = this.state.formRef;
    const qtdNum = Number(f.qtd);
    const precoNum = Number(String(f.precoUnit).replace(',', '.'));

    if (!f.refIndexPlano && f.refIndexPlano !== 0)
      return toast.warn('Selecione o item do orçamento.');
    if (!f.descricao.trim()) return toast.warn('Informe a descrição.');
    if (!Number.isFinite(qtdNum) || qtdNum <= 0)
      return toast.warn('Quantidade deve ser maior que zero.');
    if (!Number.isFinite(precoNum) || precoNum < 0)
      return toast.warn('Preço unitário inválido.');

    const base = this.state.itensBase.find(
      (i) => String(i.IndexPlano) === String(f.refIndexPlano)
    );

    this.setState({ salvando: true });
    axios
      .post(
        `${REQUEST_URL}/adendo/${f.idAdendo}/itens`,
        {
          tipo: f.tipo,
          nome: f.descricao.trim(),
          detalhes: base ? base.Detalhe : null,
          servico: base ? base.TpServico : null,
          qtd: qtdNum,
          precoUnitario: precoNum,
          precoTotal: qtdNum * precoNum,
          refIndexPlano: Number(f.refIndexPlano),
          refDescricao: base ? base.Descricao : null,
        },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({
          salvando: false,
          formRef: { ...this.state.formRef, tipo: null },
        });
        toast.success('Adendo registrado.');
        this.carregar(f.idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao registrar item.';
        toast.error(msg);
      });
  }

  // Cópia do orçamento com array de itens próprio, para que o push feito pelo
  // ModalAdicionarItem não atinja a lista do orçamento original.
  orcamentoIsolado() {
    const orc = this.props.orcamento || {};
    return {
      ...orc,
      itens: [],
      margens: { ...(orc.margens || {}) },
    };
  }

  // ModalAdicionarItem devolve o orçamento com o item novo empurrado no fim de
  // orcamento.itens. Pegamos esse último item e gravamos vinculado ao adendo,
  // em vez de deixá-lo entrar na lista do orçamento original.
  receberItemDoModal(orcamentoRetornado) {
    const idAdendo = this.state.adendoRecebendoItem;
    this.setState({ showModalAddItem: false });

    // Passamos o orçamento com itens: [], então qualquer item presente aqui é
    // o que acabou de ser escolhido. Vazio = modal fechado sem adicionar.
    const itens = (orcamentoRetornado && orcamentoRetornado.itens) || [];

    if (!idAdendo || itens.length === 0) {
      this.setState({ adendoRecebendoItem: null });
      return;
    }

    const item = itens[itens.length - 1];

    this.setState({ salvando: true });
    axios
      .post(
        `${REQUEST_URL}/adendo/${idAdendo}/itens`,
        {
          tipo: 'PRODUTO',
          IdQuote: item.IdQuote,
          IdQuoteIndigo: item.IdQuoteIndigo,
          nome: item.nome,
          detalhes: item.detalhes,
          codItem: item.codItem,
          servico: item.servico,
          qtd: item.qtd,
          qtdBrinde: item.qtdBrinde,
          precoTotalBrinde: item.precoTotalBrinde,
          // Preço já com acabamentos, como o orçamento faz ao salvar.
          precoUnitario:
            Number(item.precoUnitario || 0) +
            Number(item.precoUnitarioAcabamentos || 0),
          precoTotal:
            Number(item.precoTotal || 0) +
            Number(item.precoTotalAcabamentos || 0),
          acabamentos: item.acabamentos || [],
        },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({ salvando: false, adendoRecebendoItem: null });
        toast.success('Item adicionado ao adendo.');
        this.carregar(idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false, adendoRecebendoItem: null });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao adicionar item ao adendo.';
        toast.error(msg);
      });
  }

  iniciarEdicaoItem(item) {
    this.setState({
      editandoItemId: item.idItem,
      formItemEdicao: {
        qtd: String(item.qtd || ''),
        precoUnit: String(item.precoUnit || ''),
        qtdBrinde: String(item.qtdBrinde || 0),
        precoTotal: String(item.precoTotal || ''),
        origem: 'unit',
      },
    });
  }

  salvarEdicaoItem(idAdendo, idItem) {
    const { qtd, precoUnit, qtdBrinde, precoTotal, origem } =
      this.state.formItemEdicao;
    const qtdNum = Number(qtd);
    // Aceita vírgula decimal, como o usuário costuma digitar.
    const precoNum = Number(String(precoUnit).replace(',', '.'));
    const totalNum = Number(String(precoTotal).replace(',', '.'));
    const brindeNum = Number(qtdBrinde) || 0;

    if (!Number.isFinite(qtdNum) || qtdNum <= 0)
      return toast.warn('Quantidade deve ser maior que zero.');

    // Envia o campo que o usuário editou por último; o servidor deriva o outro.
    const payload = { qtd: qtdNum, qtdBrinde: brindeNum };
    if (origem === 'total') {
      if (!Number.isFinite(totalNum) || totalNum < 0)
        return toast.warn('Subtotal inválido.');
      payload.precoTotal = totalNum;
    } else {
      if (!Number.isFinite(precoNum) || precoNum < 0)
        return toast.warn('Preço unitário inválido.');
      payload.precoUnit = precoNum;
    }

    this.setState({ salvando: true });
    axios
      .put(
        `${REQUEST_URL}/adendo/${idAdendo}/itens/${idItem}`,
        payload,
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({ salvando: false, editandoItemId: null });
        toast.success('Item atualizado.');
        this.carregar(idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao atualizar item.';
        toast.error(msg);
      });
  }

  removerItem(idAdendo, idPlano) {
    this.setState({ salvando: true });
    axios
      .delete(`${REQUEST_URL}/adendo/${idAdendo}/itens/${idPlano}`, {
        headers: this.headers(),
      })
      .then(() => {
        this.setState({ salvando: false });
        this.carregar(idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false });
        toast.error('Erro ao remover item.');
        console.error('[TabelaAdendos] removerItem', err);
      });
  }

  aprovar(idAdendo) {
    this.setState({ salvando: true });
    axios
      .put(
        `${REQUEST_URL}/adendo/${idAdendo}/aprovar`,
        { usuario: this.props.usuarioLogado || null },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({ salvando: false });
        toast.success('Adendo aprovado.');
        this.carregar(idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao aprovar adendo.';
        toast.error(msg);
      });
  }

  iniciarEdicao(adendo) {
    this.setState({
      editandoId: adendo.idAdendo,
      formEdicao: {
        descricao: adendo.descricao || '',
        dataAdendo: dataParaInput(adendo.dataAdendo),
        motivo: adendo.motivo || '',
      },
    });
  }

  salvarEdicao(idAdendo) {
    const { descricao, dataAdendo, motivo } = this.state.formEdicao;
    if (!descricao.trim()) return toast.warn('Informe a descrição.');

    this.setState({ salvando: true });
    axios
      .put(
        `${REQUEST_URL}/adendo/${idAdendo}`,
        { descricao, dataAdendo, motivo },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({ salvando: false, editandoId: null });
        toast.success('Adendo atualizado.');
        this.carregar(idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao atualizar adendo.';
        toast.error(msg);
      });
  }

  // Exclusão definitiva: apaga o adendo, seus itens e o histórico.
  // Só aparece para ORCAMENTISTA; o servidor revalida.
  excluir(adendo) {
    const rotulo = `Adendo ${adendo.numeroAdendo}`;
    if (
      !window.confirm(
        `Excluir ${rotulo} definitivamente?\n\n` +
          'O adendo, seus itens e o histórico serão apagados e não poderão ser recuperados.\n' +
          'Para manter o registro do que aconteceu, use "Cancelar adendo".'
      )
    )
      return;

    this.setState({ salvando: true });
    axios
      .delete(`${REQUEST_URL}/adendo/${adendo.idAdendo}`, {
        headers: this.headers(),
      })
      .then(() => {
        this.setState({ salvando: false });
        toast.success(`${rotulo} excluído.`);
        this.carregar();
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao excluir adendo.';
        toast.error(msg);
      });
  }

  cancelar(idAdendo) {
    // Adendo aprovado não é apagado — é cancelado, preservando o histórico.
    if (!window.confirm('Cancelar este adendo? A ação não pode ser desfeita.'))
      return;

    this.setState({ salvando: true });
    axios
      .put(
        `${REQUEST_URL}/adendo/${idAdendo}/cancelar`,
        { usuario: this.props.usuarioLogado || null },
        { headers: this.headers() }
      )
      .then(() => {
        this.setState({ salvando: false });
        toast.info('Adendo cancelado.');
        this.carregar(idAdendo);
      })
      .catch((err) => {
        this.setState({ salvando: false });
        const msg =
          (err.response && err.response.data && err.response.data.erro) ||
          'Erro ao cancelar adendo.';
        toast.error(msg);
      });
  }

  // Formulário dos adendos que incidem sobre um item já existente:
  // ACABAMENTO (HotStamping, Verniz...) e QTD (acréscimo de quantidade).
  _renderFormReferenciado() {
    const f = this.state.formRef;
    const ehQtd = f.tipo === 'QTD';
    const base = this.state.itensBase.find(
      (i) => String(i.IndexPlano) === String(f.refIndexPlano)
    );
    const total =
      (Number(f.qtd) || 0) *
      (Number(String(f.precoUnit).replace(',', '.')) || 0);

    return (
      <div
        style={{
          background: '#eef4ff',
          border: '1px solid #b8d4ff',
          borderRadius: 6,
          padding: 12,
          marginTop: 10,
        }}
      >
        <strong style={{ fontSize: 13 }}>
          {ehQtd ? 'Acréscimo de quantidade' : 'Acabamento adicional'}
        </strong>

        <div
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}
        >
          <select
            className='form-control'
            style={{ flex: '2 1 260px' }}
            value={f.refIndexPlano}
            onChange={(e) => {
              const sel = this.state.itensBase.find(
                (i) => String(i.IndexPlano) === e.target.value
              );
              this.setState({
                formRef: {
                  ...f,
                  refIndexPlano: e.target.value,
                  // Em QTD a descrição é a do próprio item; em ACABAMENTO o
                  // usuário informa qual acabamento está sendo acrescentado.
                  descricao: ehQtd && sel ? sel.Descricao : f.descricao,
                  precoUnit:
                    ehQtd && sel ? String(sel.PrcUN || '') : f.precoUnit,
                },
              });
            }}
          >
            <option value=''>Selecione o item do orçamento *</option>
            {this.state.itensBase.map((i) => (
              <option key={i.IndexPlano} value={i.IndexPlano}>
                {i.Descricao} — {i.Detalhe || 's/ detalhe'} (qtd {i.QtdPlano})
              </option>
            ))}
          </select>

          {!ehQtd && (
            <input
              className='form-control'
              style={{ flex: '2 1 220px' }}
              list='lista-acabamentos-adendo'
              placeholder='Acabamento (HotStamping, Verniz...) *'
              value={f.descricao}
              onChange={(e) =>
                this.setState({
                  formRef: { ...f, descricao: e.target.value },
                })
              }
            />
          )}
          <datalist id='lista-acabamentos-adendo'>
            {this.state.acabamentosDisponiveis.map((a, idx) => (
              <option
                key={idx}
                value={a.DescProoduto || a.Descricao || a.Description || ''}
              />
            ))}
          </datalist>

          <input
            className='form-control'
            style={{ flex: '0 1 110px' }}
            type='number'
            min='1'
            placeholder={ehQtd ? 'Qtd a mais *' : 'Qtd *'}
            value={f.qtd}
            onChange={(e) =>
              this.setState({ formRef: { ...f, qtd: e.target.value } })
            }
          />
          <input
            className='form-control'
            style={{ flex: '0 1 130px' }}
            placeholder='Preço unit. *'
            value={f.precoUnit}
            onChange={(e) =>
              this.setState({ formRef: { ...f, precoUnit: e.target.value } })
            }
          />
        </div>

        {base && ehQtd && (
          <div style={{ fontSize: 12, color: '#6c757d', marginTop: 6 }}>
            No orçamento: {base.QtdPlano} un. Este adendo registra apenas o
            acréscimo — o item original não é alterado.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 10,
          }}
        >
          <span style={{ fontWeight: 600, color: '#007bff' }}>
            Total: {formatarMoeda(total)}
          </span>
          <span>
            <button
              className='btn btn-default btn-sm'
              style={{ marginRight: 8 }}
              onClick={() =>
                this.setState({ formRef: { ...f, tipo: null } })
              }
            >
              Cancelar
            </button>
            <button
              className='btn btn-success btn-sm'
              disabled={this.state.salvando}
              onClick={() => this.salvarItemReferenciado()}
            >
              Adicionar
            </button>
          </span>
        </div>
      </div>
    );
  }

  // Tabela de itens do adendo, no mesmo formato do card ITENS do orçamento.
  _renderItens(adendo) {
    // Rascunho é condição necessária, mas não suficiente: sem permissão de
    // edição o vendedor apenas visualiza os itens.
    const editavel = adendo.status === 'rascunho' && this.state.podeEditar;
    const itens = adendo.itens || [];
    // A liberacao na loja nao depende do adendo ser rascunho: um adendo
    // aprovado e uma venda confirmada e seus itens podem ir para a vitrine.
    // So adendo cancelado fica de fora (o backend tambem recusa).
    const mostrarLoja =
      this.state.podeEditar && adendo.status !== 'cancelado';
    const totalColunas = 6 + (mostrarLoja ? 1 : 0) + (editavel ? 1 : 0);

    return (
      <tr style={{ background: '#f8f9fa' }}>
        <td
          colSpan={this.state.podeEditar ? 7 : 6}
          style={{ padding: '0 12px 12px 44px' }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
              marginTop: 8,
            }}
          >
            <thead>
              <tr style={{ background: '#e9ecef' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left' }}>Item</th>
                <th style={{ padding: '4px 8px', textAlign: 'left' }}>
                  Detalhes
                </th>
                <th style={{ padding: '4px 8px', textAlign: 'center' }}>Qtd</th>
                <th style={{ padding: '4px 8px', textAlign: 'center' }}>
                  Qtd brinde
                </th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>Unit.</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>
                  Subtotal
                </th>
                {mostrarLoja && (
                  <th style={{ width: 90, textAlign: 'center' }}>À venda</th>
                )}
                {editavel && (
                  <th style={{ width: 80, textAlign: 'center' }}>Ações</th>
                )}
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 && (
                <tr>
                  <td
                    colSpan={totalColunas}
                    style={{ padding: 8, color: '#888' }}
                  >
                    Nenhum item neste adendo. Clique em “Adicionar ao adendo”
                    para incluir produto, acabamento ou quantidade.
                  </td>
                </tr>
              )}
              {itens.map((it) => {
                const editandoItem = this.state.editandoItemId === it.idItem;
                const fe = this.state.formItemEdicao;
                const qtdN = Number(fe.qtd) || 0;
                const unitN = Number(String(fe.precoUnit).replace(',', '.')) || 0;
                const totalN = Number(String(fe.precoTotal).replace(',', '.')) || 0;
                // Um campo alimenta o outro conforme o que foi digitado por
                // último, para os dois nunca ficarem incoerentes na tela.
                const subtotalPrevia =
                  fe.origem === 'total' ? totalN : qtdN * unitN;
                const unitPrevia =
                  fe.origem === 'total' ? (qtdN > 0 ? totalN / qtdN : 0) : unitN;

                return (
                <tr key={it.idItem} style={{ borderTop: '1px solid #dee2e6' }}>
                  <td style={{ padding: '4px 8px' }}>
                    <span
                      style={{
                        background: CORES_TIPO[it.tipo] || '#6c757d',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '1px 6px',
                        fontSize: 10,
                        marginRight: 6,
                      }}
                    >
                      {it.tipo || 'PRODUTO'}
                    </span>
                    {it.descricao}
                    {it.refDescricao && (
                      <div style={{ fontSize: 11, color: '#6c757d' }}>
                        sobre: {it.refDescricao}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '4px 8px', color: '#6c757d' }}>
                    {it.detalhe || '-'}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    {editandoItem ? (
                      <input
                        type='number'
                        min='1'
                        className='form-control form-control-sm'
                        style={{ width: 80, margin: '0 auto', textAlign: 'center' }}
                        value={fe.qtd}
                        onChange={(e) =>
                          this.setState({
                            formItemEdicao: { ...fe, qtd: e.target.value },
                          })
                        }
                      />

                    ) : (
                      it.qtd
                    )}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    {editandoItem ? (
                      <input
                        type='number'
                        min='0'
                        className='form-control form-control-sm'
                        style={{ width: 80, margin: '0 auto', textAlign: 'center' }}
                        value={fe.qtdBrinde}
                        onChange={(e) =>
                          this.setState({
                            formItemEdicao: { ...fe, qtdBrinde: e.target.value },
                          })
                        }
                      />
                    ) : (
                      it.qtdBrinde || 0
                    )}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                    {editandoItem ? (
                      <input
                        className='form-control form-control-sm'
                        style={{ width: 110, marginLeft: 'auto', textAlign: 'right' }}
                        // Se o subtotal foi o último editado, este campo passa
                        // a espelhar o unitário derivado.
                        value={
                          fe.origem === 'total'
                            ? unitPrevia.toFixed(2)
                            : fe.precoUnit
                        }
                        onChange={(e) =>
                          this.setState({
                            formItemEdicao: {
                              ...fe,
                              precoUnit: e.target.value,
                              origem: 'unit',
                            },
                          })
                        }
                      />
                    ) : (
                      formatarMoeda(it.precoUnit)
                    )}
                  </td>
                  <td
                    style={{
                      padding: '4px 8px',
                      textAlign: 'right',
                    }}
                  >
                    {editandoItem ? (
                      <input
                        className='form-control form-control-sm'
                        style={{
                          width: 120,
                          marginLeft: 'auto',
                          textAlign: 'right',
                          fontWeight: 600,
                        }}
                        title='Digite o subtotal para fechar o valor; o unitário é recalculado'
                        value={
                          fe.origem === 'total'
                            ? fe.precoTotal
                            : subtotalPrevia.toFixed(2)
                        }
                        onChange={(e) =>
                          this.setState({
                            formItemEdicao: {
                              ...fe,
                              precoTotal: e.target.value,
                              origem: 'total',
                            },
                          })
                        }
                      />
                    ) : (
                      formatarMoeda(it.precoTotal)
                    )}
                  </td>
                  {mostrarLoja && (
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      {(() => {
                        const naLoja = Number(it.naLoja) === 1;
                        const salvandoLoja =
                          this.state.weblojaSalvandoRef ===
                          OFFSET_ADENDO + it.idItem;
                        return (
                          <button
                            className={
                              naLoja
                                ? 'btn btn-success btn-sm'
                                : 'btn btn-default btn-sm'
                            }
                            disabled={salvandoLoja}
                            title={
                              naLoja
                                ? 'Clique para tirar da loja'
                                : 'Clique para liberar na loja'
                            }
                            onClick={() =>
                              this.alternarItemAdendoNaLoja(
                                adendo.idAdendo,
                                it,
                                !naLoja
                              )
                            }
                          >
                            {salvandoLoja ? (
                              <i className='fa fa-spinner fa-pulse' />
                            ) : naLoja ? (
                              <React.Fragment>
                                <i className='fas fa-check' /> Sim
                              </React.Fragment>
                            ) : (
                              'Não'
                            )}
                          </button>
                        );
                      })()}
                    </td>
                  )}
                  {editavel && (
                    <td
                      style={{
                        padding: '4px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {editandoItem ? (
                        <React.Fragment>
                          <i
                            className='fas fa-check'
                            title='Salvar item'
                            style={{
                              color: '#27ae60',
                              cursor: 'pointer',
                              marginRight: 10,
                            }}
                            onClick={() =>
                              this.salvarEdicaoItem(adendo.idAdendo, it.idItem)
                            }
                          />
                          <i
                            className='fas fa-times'
                            title='Cancelar edição'
                            style={{ color: '#6c757d', cursor: 'pointer' }}
                            onClick={() =>
                              this.setState({ editandoItemId: null })
                            }
                          />
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <i
                            className='fas fa-pencil-alt'
                            title='Editar quantidade e preço'
                            style={{
                              color: '#007bff',
                              cursor: 'pointer',
                              marginRight: 10,
                            }}
                            onClick={() => this.iniciarEdicaoItem(it)}
                          />
                          <i
                            className='fas fa-trash'
                            title='Remover item'
                            style={{ color: '#c0392b', cursor: 'pointer' }}
                            onClick={() =>
                              this.removerItem(adendo.idAdendo, it.idItem)
                            }
                          />
                        </React.Fragment>
                      )}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>

          {editavel && this.state.formRef.tipo &&
            this.state.formRef.idAdendo === adendo.idAdendo &&
            this._renderFormReferenciado()}

          {editavel && (
            <div style={{ marginTop: 10 }}>
              {this.state.tipoEscolhaDe === adendo.idAdendo ? (
                <div
                  style={{
                    display: 'inline-flex',
                    gap: 8,
                    background: '#eef4ff',
                    border: '1px solid #b8d4ff',
                    borderRadius: 6,
                    padding: 8,
                  }}
                >
                  <span style={{ alignSelf: 'center', fontSize: 12 }}>
                    O que o adendo inclui?
                  </span>
                  <button
                    className='btn btn-success btn-sm'
                    onClick={() => this.escolherTipo(adendo.idAdendo, 'PRODUTO')}
                  >
                    Produto
                  </button>
                  <button
                    className='btn btn-info btn-sm'
                    onClick={() =>
                      this.escolherTipo(adendo.idAdendo, 'ACABAMENTO')
                    }
                  >
                    Acabamento
                  </button>
                  <button
                    className='btn btn-warning btn-sm'
                    onClick={() => this.escolherTipo(adendo.idAdendo, 'QTD')}
                  >
                    Quantidade
                  </button>
                  <button
                    className='btn btn-default btn-sm'
                    onClick={() => this.setState({ tipoEscolhaDe: null })}
                  >
                    Fechar
                  </button>
                </div>
              ) : (
                <button
                  className='btn btn-success btn-sm'
                  style={{ borderRadius: '.750rem' }}
                  onClick={() => this.abrirEscolhaTipo(adendo.idAdendo)}
                >
                  {/* Rótulo distinto do "+ Item" do card ITENS do orçamento,
                      que fica logo acima e abre outro fluxo. */}
                  <i className='fas fa-plus' /> Adicionar ao adendo
                </button>
              )}
              <button
                className='btn btn-primary btn-sm'
                style={{ marginLeft: 8 }}
                disabled={this.state.salvando || itens.length === 0}
                title={
                  itens.length === 0
                    ? 'Adicione ao menos um item para aprovar'
                    : 'Aprovar adendo'
                }
                onClick={() => this.aprovar(adendo.idAdendo)}
              >
                <i className='fas fa-check' /> Aprovar
              </button>
              <button
                className='btn btn-link btn-sm'
                style={{ marginLeft: 8, color: '#c0392b' }}
                onClick={() => this.cancelar(adendo.idAdendo)}
              >
                Cancelar adendo
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  }

  renderWebloja() {
    const { weblojaItens, weblojaCarregando, weblojaSalvandoRef } = this.state;
    const ativos = weblojaItens.filter((i) => Number(i.ativo) === 1).length;

    return (
      <div
        style={{
          border: '1px solid #b8daff',
          background: '#f1f8ff',
          borderRadius: 6,
          padding: 12,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <strong style={{ color: '#004085' }}>
            <i className='fas fa-store' style={{ marginRight: 6 }} />
            Itens à venda na web loja
            {ativos > 0 && (
              <span
                style={{
                  marginLeft: 8,
                  background: '#007bff',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '1px 8px',
                  fontSize: 12,
                }}
              >
                {ativos} ativo{ativos > 1 ? 's' : ''}
              </span>
            )}
          </strong>
          <button
            className='btn btn-default btn-sm'
            onClick={() => this.carregarWebloja()}
            disabled={weblojaCarregando}
            title='Recarregar'
          >
            <i className='fas fa-sync' />
          </button>
        </div>

        <div style={{ fontSize: 12, color: '#31708f', marginBottom: 10 }}>
          Ao liberar um item, a turma passa a comprá-lo na loja. Cada compra
          entra automaticamente num adendo aprovado deste contrato.
        </div>

        {weblojaCarregando ? (
          <p style={{ color: '#666', margin: 0 }}>Carregando itens...</p>
        ) : weblojaItens.length === 0 ? (
          <p style={{ color: '#888', margin: 0 }}>
            Este orçamento não tem itens para publicar.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
                background: '#fff',
              }}
            >
              <thead>
                <tr style={{ background: '#e9ecef' }}>
                  <th style={{ padding: '6px 12px', textAlign: 'left' }}>
                    Item
                  </th>
                  <th style={{ padding: '6px 12px', textAlign: 'right' }}>
                    Preço do contrato
                  </th>
                  <th style={{ padding: '6px 12px', textAlign: 'right' }}>
                    Preço na loja
                  </th>
                  <th style={{ padding: '6px 12px', textAlign: 'center' }}>
                    À venda
                  </th>
                </tr>
              </thead>
              <tbody>
                {weblojaItens.map((item) => {
                  const ativo = Number(item.ativo) === 1;
                  const salvando = weblojaSalvandoRef === item.refIndexPlano;
                  return (
                    <tr
                      key={item.refIndexPlano}
                      style={{ borderTop: '1px solid #dee2e6' }}
                    >
                      <td style={{ padding: '6px 12px' }}>
                        {/* Itens vindos de adendo se misturariam aos do
                            contrato sem esta marca. */}
                        {item.origem === 'ADENDO' && (
                          <span
                            style={{
                              background: '#6f42c1',
                              color: '#fff',
                              borderRadius: 8,
                              padding: '1px 6px',
                              fontSize: 10,
                              marginRight: 6,
                            }}
                          >
                            ADENDO {item.numeroAdendo}
                          </span>
                        )}
                        {item.descricao}
                        {item.detalhe ? (
                          <span style={{ color: '#6c757d' }}>
                            {' '}
                            — {item.detalhe}
                          </span>
                        ) : null}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                        {formatarMoeda(item.precoContrato)}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                        {item.precoLoja == null ? (
                          <span style={{ color: '#6c757d' }}>
                            {formatarMoeda(item.precoContrato)}
                          </span>
                        ) : (
                          formatarMoeda(item.precoLoja)
                        )}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                        <button
                          className={
                            ativo
                              ? 'btn btn-success btn-sm'
                              : 'btn btn-default btn-sm'
                          }
                          disabled={salvando}
                          onClick={() =>
                            this.alternarItemWebloja(item, !ativo)
                          }
                          title={
                            ativo
                              ? 'Clique para tirar da loja'
                              : 'Clique para liberar na loja'
                          }
                        >
                          {salvando ? (
                            <i className='fa fa-spinner fa-pulse' />
                          ) : ativo ? (
                            <>
                              <i className='fas fa-check' /> Sim
                            </>
                          ) : (
                            'Não'
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { adendos, carregando, expandidos, salvando } = this.state;
    const totalAprovado = adendos
      .filter((a) => a.status === 'aprovado')
      .reduce((acc, a) => acc + Number(a.valorTotal || 0), 0);

    return (
      <Card
        style={{
          border: 'none',
          borderRadius: '.375rem',
          boxShadow:
            '0.5rem 0.5rem 2rem 0 rgb(8 15 41 / 8%), 0 0 1px 0 rgb(8 15 41 / 8%)',
        }}
      >
        <CardHeader
          style={{
            borderBottom: 'none',
            backgroundColor: 'white',
            borderRadius: '.375rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>
            ADENDOS{' '}
            {adendos.length > 0 && (
              <span
                style={{
                  marginLeft: 6,
                  background: '#007bff',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '1px 8px',
                  fontSize: 12,
                }}
              >
                {adendos.length}
              </span>
            )}
          </span>
          {this.state.podeEditar && (
            <span style={{ display: 'flex', gap: 8 }}>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '.750rem',
                }}
                className={
                  this.state.mostrarWebloja
                    ? 'btn btn-primary'
                    : 'btn btn-outline-primary'
                }
                title='Escolher quais itens deste contrato a turma pode comprar na web loja'
                onClick={() => this.alternarWebloja()}
              >
                <i className='fas fa-store' />
                <div className='hidden-text' style={{ marginLeft: 5 }}>
                  Web loja
                </div>
              </button>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '.750rem',
                }}
                className='btn btn-success'
                disabled={salvando}
                title='Cria um adendo com a data de hoje e abre a seleção de itens'
                onClick={() => this.criarAdendo()}
              >
                <i className='fas fa-plus' />
                <div className='hidden-text' style={{ marginLeft: 5 }}>
                  Adendo
                </div>
              </button>
            </span>
          )}
        </CardHeader>

        <CardBody style={{ paddingTop: 0 }}>
          {this.state.mostrarWebloja && this.renderWebloja()}
          {carregando ? (
            <p style={{ color: '#666', margin: 0 }}>Carregando adendos...</p>
          ) : adendos.length === 0 ? (
            <p style={{ color: '#888', margin: 0 }}>
              Nenhum adendo registrado para este contrato.
            </p>
          ) : (
            <div
              style={{
                border: '1px solid #dee2e6',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: '#e9ecef' }}>
                    <th style={{ padding: '6px 12px', width: 32 }} />
                    <th style={{ padding: '6px 12px', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '6px 12px', textAlign: 'left' }}>
                      Descrição
                    </th>
                    <th style={{ padding: '6px 12px', textAlign: 'left' }}>
                      Data
                    </th>
                    <th style={{ padding: '6px 12px', textAlign: 'right' }}>
                      Valor
                    </th>
                    <th style={{ padding: '6px 12px', textAlign: 'center' }}>
                      Status
                    </th>
                    {this.state.podeEditar && (
                      <th style={{ padding: '6px 12px', width: 80 }}>Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {adendos.map((adendo, i) => {
                    const aberto = !!expandidos[adendo.idAdendo];
                    const cancelado = adendo.status === 'cancelado';
                    const qtdItens = (adendo.itens || []).length;
                    const editando = this.state.editandoId === adendo.idAdendo;
                    // Cancelado é imutável: preserva o histórico.
                    const podeEditarEste = this.state.podeEditar && !cancelado;
                    return (
                      <React.Fragment key={adendo.idAdendo}>
                        <tr
                          style={{
                            borderTop: '1px solid #dee2e6',
                            background: i % 2 === 0 ? '#fff' : '#f8f9fa',
                            cursor: 'pointer',
                            opacity: cancelado ? 0.55 : 1,
                          }}
                          onClick={() => this.toggleExpandir(adendo.idAdendo)}
                        >
                          <td
                            style={{
                              padding: '6px 12px',
                              textAlign: 'center',
                              color: '#6c757d',
                              fontSize: 11,
                            }}
                          >
                            {aberto ? '▲' : '▼'}
                          </td>
                          <td style={{ padding: '6px 12px', fontWeight: 600 }}>
                            {adendo.numeroAdendo}
                          </td>
                          <td
                            style={{ padding: '6px 12px' }}
                            onClick={(e) => editando && e.stopPropagation()}
                          >
                            {editando ? (
                              <input
                                className='form-control form-control-sm'
                                autoFocus
                                value={this.state.formEdicao.descricao}
                                onChange={(e) =>
                                  this.setState({
                                    formEdicao: {
                                      ...this.state.formEdicao,
                                      descricao: e.target.value,
                                    },
                                  })
                                }
                              />
                            ) : (
                              <React.Fragment>
                                {adendo.descricao}
                                {adendo.motivo && (
                                  <span
                                    style={{ color: '#6c757d', fontSize: 11 }}
                                  >
                                    {' '}
                                    ({adendo.motivo})
                                  </span>
                                )}
                                <span
                                  style={{ color: '#6c757d', fontSize: 11 }}
                                >
                                  {' '}
                                  — {qtdItens}{' '}
                                  {qtdItens === 1 ? 'item' : 'itens'}
                                </span>
                              </React.Fragment>
                            )}
                          </td>
                          <td
                            style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}
                            onClick={(e) => editando && e.stopPropagation()}
                          >
                            {editando ? (
                              <input
                                type='date'
                                className='form-control form-control-sm'
                                value={this.state.formEdicao.dataAdendo}
                                onChange={(e) =>
                                  this.setState({
                                    formEdicao: {
                                      ...this.state.formEdicao,
                                      dataAdendo: e.target.value,
                                    },
                                  })
                                }
                              />
                            ) : (
                              formatarData(adendo.dataAdendo)
                            )}
                          </td>
                          <td
                            style={{
                              padding: '6px 12px',
                              textAlign: 'right',
                              fontWeight: 600,
                              textDecoration: cancelado
                                ? 'line-through'
                                : 'none',
                            }}
                          >
                            {formatarMoeda(adendo.valorTotal)}
                          </td>
                          <td
                            style={{ padding: '6px 12px', textAlign: 'center' }}
                          >
                            <span
                              style={{
                                background:
                                  CORES_STATUS[adendo.status] || '#6c757d',
                                color: '#fff',
                                borderRadius: 12,
                                padding: '2px 10px',
                                fontSize: 11,
                                textTransform: 'capitalize',
                              }}
                            >
                              {adendo.status}
                            </span>
                          </td>
                          {this.state.podeEditar && (
                            <td
                              style={{
                                padding: '6px 12px',
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                              }}
                              /* impede que o clique também expanda a linha */
                              onClick={(e) => e.stopPropagation()}
                            >
                              {editando ? (
                                <React.Fragment>
                                  <i
                                    className='fas fa-check'
                                    title='Salvar'
                                    style={{
                                      color: '#27ae60',
                                      cursor: 'pointer',
                                      marginRight: 12,
                                    }}
                                    onClick={() =>
                                      this.salvarEdicao(adendo.idAdendo)
                                    }
                                  />
                                  <i
                                    className='fas fa-times'
                                    title='Cancelar edição'
                                    style={{ color: '#6c757d', cursor: 'pointer' }}
                                    onClick={() =>
                                      this.setState({ editandoId: null })
                                    }
                                  />
                                </React.Fragment>
                              ) : (
                                <React.Fragment>
                                  {podeEditarEste && (
                                    <i
                                      className='fas fa-pencil-alt'
                                      title='Editar descrição e data'
                                      style={{
                                        color: '#007bff',
                                        cursor: 'pointer',
                                        marginRight: 12,
                                      }}
                                      onClick={() => this.iniciarEdicao(adendo)}
                                    />
                                  )}
                                  {this.state.podeExcluir && (
                                    <i
                                      className='fas fa-trash'
                                      title='Excluir adendo definitivamente'
                                      style={{
                                        color: '#c0392b',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => this.excluir(adendo)}
                                    />
                                  )}
                                </React.Fragment>
                              )}
                            </td>
                          )}
                        </tr>

                        {aberto && this._renderItens(adendo)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      borderTop: '2px solid #dee2e6',
                      background: '#f8f9fa',
                    }}
                  >
                    <td
                      colSpan={4}
                      style={{
                        padding: '8px 12px',
                        textAlign: 'right',
                        fontWeight: 700,
                      }}
                    >
                      Total dos adendos aprovados:
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#27ae60',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatarMoeda(totalAprovado)}
                    </td>
                    <td colSpan={this.state.podeEditar ? 2 : 1} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardBody>

        {/* Mesmo modal de produtos usado pelo card ITENS do orçamento. */}
        <Draggable handle='.modal-header'>
          <Modal
            className='no-print'
            isOpen={this.state.showModalAddItem}
            size='lg'
            id='inserir_item_adendo'
          >
            <ModalAdicionarItem
              itemFuturo={0}
              /* ModalAdicionarItem faz Object.assign({}, orcamento) — cópia
                 rasa — e dá push em orcamento.itens. Como o array seria o
                 MESMO do orçamento real, o item do adendo apareceria no card
                 ITENS. Passamos uma cópia com array próprio para isolar. */
              orcamento={this.orcamentoIsolado()}
              criarLog={this.props.criarLog}
              close={(orc) => this.receberItemDoModal(orc)}
              possuiArte={this.props.possuiArte}
              possuiFrete={this.props.possuiFrete}
              possuiPagamentoIndividual={this.props.possuiPagamentoIndividual}
              listaPrecosArte={this.props.listaPrecosArte}
              listaPrecosFrete={this.props.listaPrecosFrete}
            />
          </Modal>
        </Draggable>
      </Card>
    );
  }
}

export { TabelaAdendos };
export default TabelaAdendos;
